import { useState } from 'react';
import { PlanInfo } from '@/lib/planConfig';
import { fmtMoeda } from '@/pages/plan/shared';

interface PlanoEscolhaProps {
  planos: PlanInfo[];
  titular?: string;
  /** Executa o checkout. Devolve uma mensagem de erro, ou nada quando redireciona com sucesso. */
  onCheckout: (planoId: string) => Promise<string | void>;
  initialPlanoId?: string | null;
}

// Extraido do passo 2 de `RegisterPage` (P-41): a mesma escolha de plano e
// checkout precisava ser usada tambem por quem ja tem conta e so nao tem
// assinatura (`/gestao-plano/plano`). O componente nao conhece a origem —
// quem chama decide o `onCheckout` (URLs de sucesso/cancelamento incluidas).
export default function PlanoEscolha({
  planos,
  titular,
  onCheckout,
  initialPlanoId = null,
}: PlanoEscolhaProps) {
  const [planoSelecionado, setPlanoSelecionado] = useState<string | null>(initialPlanoId);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');

  const planoEscolhido = planos.find((p) => p.id === planoSelecionado);

  const handleCheckout = async () => {
    if (!planoSelecionado) {
      setError('Selecione um plano antes de continuar.');
      return;
    }
    setError('');
    setCheckoutLoading(true);
    const errMsg = await onCheckout(planoSelecionado);
    if (errMsg) {
      setError(errMsg);
      setCheckoutLoading(false);
    }
    // Sem erro, `onCheckout` ja redirecionou para o Stripe: mantem o loading
    // ate a navegacao acontecer, em vez de piscar o botao de volta ao normal.
  };

  return (
    <div className="w-full">
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5 max-w-lg mx-auto">
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
        </div>
      )}

      {/* Plan cards - side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4 mb-8">
        {planos.map((plano) => (
          <div
            key={plano.id}
            onClick={() => {
              setPlanoSelecionado(plano.id);
              setError('');
            }}
            className={`relative rounded-2xl border-2 p-5 transition-all cursor-pointer flex flex-col h-full ${
              planoSelecionado === plano.id
                ? 'border-primary-500 bg-primary-50/30'
                : 'border-background-200/70 bg-background-50 hover:border-background-300'
            }`}
          >
            {/* Badge */}
            {plano.popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-semibold bg-accent-500 text-background-50 whitespace-nowrap">
                Mais popular
              </span>
            )}
            {plano.destaque && !plano.popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-semibold bg-primary-100 text-primary-700 whitespace-nowrap">
                Destaque
              </span>
            )}

            {/* Selection indicator */}
            {planoSelecionado === plano.id && (
              <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-check-line text-background-50 text-sm"></i>
              </div>
            )}

            <div className="flex-1">
              <h3 className="text-lg font-bold text-foreground-900 mb-1">{plano.nome}</h3>
              <p className="text-sm text-foreground-500 mb-4 line-clamp-2">{plano.descricao}</p>

              <div className="mb-4">
                <span className="text-3xl font-bold text-foreground-900">
                  {fmtMoeda(plano.preco)}
                </span>
                <span className="text-sm text-foreground-500">/mês</span>
              </div>

              <p className="text-xs text-foreground-500 mb-3">
                {plano.condominiosMax === 1
                  ? '1 condomínio'
                  : `Até ${plano.condominiosMax} condomínios`}
              </p>

              <div className="flex flex-col gap-2 mb-4">
                {plano.recursos.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center gap-1.5 text-xs text-foreground-600"
                  >
                    <i className="ri-check-line text-primary-500 text-sm"></i>
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom action area */}
      <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 max-w-4xl mx-auto">
        {/* Checkout summary */}
        {planoEscolhido && (
          <div className="rounded-xl border border-background-200/70 bg-background-100/50 p-5 flex-1 w-full lg:w-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-foreground-700">Resumo do pedido</span>
              <span className="text-xs text-foreground-500">Cobrança imediata</span>
            </div>
            <div className="flex items-center justify-between pb-3 border-b border-background-200/70">
              <div>
                <p className="text-sm font-semibold text-foreground-900">
                  Plano {planoEscolhido.nome}
                </p>
                {titular && <p className="text-xs text-foreground-500">{titular}</p>}
              </div>
              <span className="text-sm font-bold text-foreground-900">
                {fmtMoeda(planoEscolhido.preco)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-3">
              <span className="text-sm font-semibold text-foreground-900">Total hoje</span>
              <span className="text-lg font-bold text-primary-600">
                {fmtMoeda(planoEscolhido.preco)}
              </span>
            </div>
          </div>
        )}

        {/* Pay button */}
        <div className="flex flex-col gap-3 w-full lg:w-auto lg:min-w-[280px]">
          <button
            type="button"
            onClick={handleCheckout}
            disabled={!planoSelecionado || checkoutLoading}
            className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
          >
            {checkoutLoading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Redirecionando para o Stripe...
              </>
            ) : (
              <>
                <i className="ri-bank-card-line"></i>
                Pagar com Stripe
              </>
            )}
          </button>

          <div className="flex items-center justify-center gap-4">
            <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
              <i className="ri-visa-line"></i> Cartão
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
              <i className="ri-barcode-line"></i> Boleto
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
              <i className="ri-qr-code-line"></i> Pix
            </span>
          </div>

          <p className="text-center text-[11px] text-foreground-400">
            Pagamento seguro via Stripe. Cancele a qualquer momento.
          </p>

          {!planoSelecionado && (
            <p className="text-center text-xs text-foreground-400">
              Selecione um plano acima para continuar.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
